import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TaskInstancesResponse } from '@app/_models/task-instances-response';
import { ProcessInstancesResponse } from '@app/_models/process-instances-response';
import { ProcessInstance } from '@app/_models/process-instances-response';
import { environment } from 'environments/environment';
import { TaskHistoryInstancesResponse } from "@app/_models/task-history-instances-response";
import { PagedData } from '@app/_models/paged-data.model';
import { Task } from '@app/_models/task';

@Injectable({
  providedIn: 'root'
})
export class BpmService {
  private http = inject(HttpClient);


  startProcess(startData: any): Observable<ProcessInstance> {
    return this.http.post<ProcessInstance>(`${environment.apiUrl}/api/process-api/runtime/process-instances`, startData);
  }

  tasksLegacy(): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks`);
  }

  task(id?: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/process-api/runtime/tasks/${id}?includeProcessVariables=true`);
  }

  historicTask(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/process-api/history/historic-task-instances/${id}?includeProcessVariables=true`);
  }

  tasksByProcessInstanceId(processInstanceId: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?processInstanceId=${processInstanceId}`);
  }

  historicTasksByProcessInstanceId(processInstanceId: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-task-instances?processInstanceId=${processInstanceId}&sort=startTime&order=asc&size=20`);
  }

  historicTasksByProcessInstanceBusinessKey(processBusinessKey: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-task-instances?processBusinessKey=${processBusinessKey}`);
  }

  historicTasksByCandidateOrAssignedPageable(assignee: string, start: number, size: number): Observable<TaskHistoryInstancesResponse> {
    return this.http.get<TaskHistoryInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-task-instances?taskAssignee=${assignee}&finished=true&sort=startTime&order=desc&start=${start}&size=${size}`);
  }

  historicActivitiesByProcessInstanceId(processInstanceId: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-activity-instances?processInstanceId=${processInstanceId}&sort=startTime&order=asc`);
  }

  tasksByCandidateGroup(groupName: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateGroup=${groupName}`);
  }

  tasksByFormKey(groupName: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateGroup=${groupName}`);
  }

  tasksByName(name: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?name=${name}&size=1000`);
  }

  tasksByDefinitionKeyAndProcessInstanceBusinessKey(definitionKey: string, businessKey: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?processInstanceBusinessKey=${businessKey}&taskDefinitionKey=${definitionKey}`);
  }

  tasksByAssignedAndDefinitionKeyAndProcessInstanceBusinessKeyAndActive(assignee: string, definitionKey: string, businessKey: string, active: boolean): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateOrAssigned=${assignee}&processInstanceBusinessKey=${businessKey}&taskDefinitionKey=${definitionKey}&active=${active}`);
  }

  tasks(queryStr: string, start: number, size: number): Observable<PagedData<Task>> {
    if (queryStr && queryStr.length > 0) {
      queryStr += '&';
    }
    return this.http.get<PagedData<Task>>(`${environment.apiUrl}/api/bpm/tasks?${queryStr}start=${start}&size=${size}`);
  }

  complete(id: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/bpm/complete/tasks/${id}`);
  }

  completeWithVars(id: string, vars: any[]): Observable<any> {
    const action = {
      action: 'complete',
      variables: vars
    };
    console.log(action);
    return this.http.post<any>(`${environment.apiUrl}/api/process-api/runtime/tasks/${id}`, action);
  }

  tasksAssigned(assignee: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?assignee=${assignee}&size=500`);
  }

  tasksCandidate(assignee: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateUser=${assignee}&size=500`);
  }

  tasksCandidateOrAssigned(assignee: string): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateOrAssigned=${assignee}&size=500`);
  }

  tasksCandidateOrAssignedPageable(assignee: string, start: number, size: number): Observable<TaskInstancesResponse> {
    return this.http.get<TaskInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/tasks?candidateOrAssigned=${assignee}&active=true&sort=createTime&order=desc&start=${start}&size=${size}&includeProcessVariables=true`);
  }

  processInstances(): Observable<ProcessInstancesResponse> {
    return this.http.get<ProcessInstancesResponse>(`${environment.apiUrl}/api/process-api/runtime/process-instances?includeProcessVariables=true&size=1000`);
  }

  historicProcessInstancesQuery(queryStr: string, start: number, size: number): Observable<ProcessInstancesResponse> {
    return this.http.get<ProcessInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-process-instances?${queryStr}&sort=startTime&order=desc&start=${start}&size=${size}`);
  }

  historicProcessInstancesByBusinessKey(businessKey: string): Observable<ProcessInstancesResponse> {
    return this.http.get<ProcessInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-process-instances?businessKey=${businessKey}`);
  }

  historicProcessInstance(processInstanceId: string): Observable<ProcessInstancesResponse> {
    return this.http.get<ProcessInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-process-instances/${processInstanceId}?includeProcessVariables=true`);
  }

  processInstance(processInstanceId: string): Observable<ProcessInstance> {
    return this.http.get<ProcessInstance>(`${environment.apiUrl}/api/process-api/runtime/process-instances/${processInstanceId}?includeProcessVariables=true`);
  }

  involvedUsers(processInstanceId: string): Observable<ProcessInstance> {
    return this.http.get<ProcessInstance>(`${environment.apiUrl}/api/process-api/runtime/process-instances/${processInstanceId}/identitylinks`);
  }

  identityLinks(taskId: string): Observable<ProcessInstance> {
    return this.http.get<ProcessInstance>(`${environment.apiUrl}/api/process-api/runtime/tasks/${taskId}/identitylinks`);
  }

  processDefinition(processDefinitionId: string): Observable<any> {
    return this.http.get<ProcessInstance>(`${environment.apiUrl}/api/process-api/repository/process-definitions/${processDefinitionId}`);
  }

  user(userId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/process-api/identity/users/${userId}`);
  }

  historicVariablesByProcessInstanceId(processInstanceId: string) {
    return this.http.get<ProcessInstancesResponse>(`${environment.apiUrl}/api/process-api/history/historic-variable-instances?processInstanceId=${processInstanceId}`);
  }

  suspendProcessInstance(processInstanceId: any) {
    const data = {action: 'suspend'};
    return this.http.put(`${environment.apiUrl}/api/process-api/runtime/process-instances/${processInstanceId}`, data);
  }

  resumeProcessInstance(processInstanceId: any) {
    const data = {action: 'activate'};
    return this.http.put(`${environment.apiUrl}/api/process-api/runtime/process-instances/${processInstanceId}`, data);
  }

  deleteProcessInstance(processInstanceId: any) {
    return this.http.delete(`${environment.apiUrl}/api/bpm/process-instance/${processInstanceId}`);
  }

  variables(taskId: string) {
    return this.http.get(`${environment.apiUrl}/api/process-api/runtime/tasks/${taskId}/variables`);
  }

  setAssignee(taskId: any, assigneeData: any) {
    return this.http.post(`${environment.apiUrl}/api/admin/bpm/assignee/${taskId}`, assigneeData);
  }

  deleteAssignee(taskId: any) {
    return this.http.delete(`${environment.apiUrl}/api/admin/bpm/assignee/${taskId}`);
  }

  findTasksByQuery(searchQuery: any) {
    return this.http.post(`${environment.apiUrl}/api/process-api/query/tasks`, searchQuery);
  }
}
